import React, { useState, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { TransportFormData, TransportLeg } from "./transportTypes";
import { TransportData } from "@/types/trip";
import { transportService } from "@/services/transportService";
import { debounce } from "lodash";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Loader2 } from "lucide-react";
import { Location } from "@/types/trip";

interface StopoverSelectorProps {
  formData: TransportFormData;
  handleInputChange: (field: string, value: any) => void;
  transportData: TransportData;
}

interface AirportData {
  code: string;
  name: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: string;
}

interface AirportResult {
  data: AirportData[];
  success: boolean;
}

interface CacheItem {
  data: AirportData[];
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MIN_SEARCH_LENGTH = 2;

const StopoverSelector: React.FC<StopoverSelectorProps> = ({
  formData,
  handleInputChange,
  transportData,
}) => {
  const numLegs = Number(formData.numberOfLegs);
  const stopovers = numLegs - 1;

  const [searchQueries, setSearchQueries] = useState<Record<number, string>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<AirportData[]>(
    []
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const getInputValue = (index: number) => {
    if (formData.legs[index]?.destination) {
      const leg = formData.legs[index];
      return leg.destinationCode
        ? `${leg.destination} (${leg.destinationCode})`
        : leg.destination;
    }
    return searchQueries[index] || "";
  };

  const debouncedSearch = useCallback(
    debounce(async (query: string, index: number) => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setAutocompleteResults([]);
        setIsLoading(false);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      try {
        setIsLoading(true);
        const response = await transportService.airportSearch(query);
        const results = response.data.data;

        setAutocompleteResults(results);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Error searching airports:", error);
          setAutocompleteResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    }, 200),
    []
  );

  const updateLeg = useCallback(
    (legs: TransportLeg[], index: number, airport?: AirportData) => {
      const newLegs = [...legs];
      const currentTime = new Date().toISOString();

      // Helper function to create a leg with default values
      const createLeg = (
        origin: string,
        destination: string,
        originCode?: string,
        destinationCode?: string,
        originTimezone?: string,
        destinationTimezone?: string
      ): TransportLeg => ({
        origin,
        destination,
        originCode,
        destinationCode,
        originTimezone,
        destinationTimezone,
        mode: "flight",
        departureTime: currentTime,
        arrivalTime: currentTime,
      });

      if (airport) {
        newLegs[index] = createLeg(
          airport.name,
          airport.name,
          airport.code,
          airport.code,
          airport.timezone,
          airport.timezone
        );
      }

      // Update the chain of legs
      newLegs.forEach((_, i) => {
        if (i === 0) {
          // For first leg, use locationAbove's name and first airport code
          newLegs[i] = createLeg(
            transportData.locationAbove?.name || "",
            newLegs[i]?.destination || "",
            transportData.locationAbove?.airports?.[0] || "",
            newLegs[i]?.destinationCode || "",
            newLegs[i]?.originTimezone,
            newLegs[i]?.destinationTimezone
          );
        } else {
          // For intermediate legs, use previous leg's destination
          newLegs[i] = createLeg(
            newLegs[i - 1]?.destination || "",
            newLegs[i]?.destination || "",
            newLegs[i - 1]?.destinationCode || "",
            newLegs[i]?.destinationCode || "",
            newLegs[i - 1]?.destinationTimezone,
            newLegs[i]?.destinationTimezone
          );
        }
      });

      // Set final destination using locationBelow's name and first airport code
      newLegs[newLegs.length - 1] = createLeg(
        newLegs[newLegs.length - 1]?.origin || "",
        transportData.locationBelow?.name || "",
        newLegs[newLegs.length - 1]?.originCode || "",
        transportData.locationBelow?.airports?.[0] || "",
        newLegs[newLegs.length - 1]?.originTimezone,
        newLegs[newLegs.length - 1]?.destinationTimezone
      );

      return newLegs;
    },
    [transportData]
  );

  const handleStopoverChange = (index: number, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [index]: value }));
    setActiveIndex(index);
    debouncedSearch(value, index);
  };

  const handleAirportSelect = (index: number, airport: AirportData) => {
    const updatedLegs = [...formData.legs];
    const currentLeg = updatedLegs[index];

    // Update the selected leg's destination and the next leg's origin
    if (currentLeg) {
      updatedLegs[index] = {
        ...currentLeg,
        destination: airport.name,
        destinationCode: airport.code,
        destinationCoordinates: airport.coordinates,
        destinationTimezone: airport.timezone,
      };

      // Update the next leg's origin if it exists
      const nextLeg = updatedLegs[index + 1];
      if (nextLeg) {
        updatedLegs[index + 1] = {
          ...nextLeg,
          origin: airport.name,
          originCode: airport.code,
          originCoordinates: airport.coordinates,
          originTimezone: airport.timezone,
        };
      }
    }

    handleInputChange("legs", updatedLegs);
    setSearchQueries((prev) => ({ ...prev, [index]: "" }));
    setActiveIndex(null);
    setAutocompleteResults([]);
  };

  const handleStopoverSelect = (
    selectedOption: { value: string; label: string } | null,
    legIndex: number
  ) => {
    if (!selectedOption) return;

    const selectedLocation = transportData.locations?.find(
      (loc: Location) => loc.id === selectedOption.value
    );
    if (!selectedLocation) return;

    const updatedLegs = [...formData.legs];
    const currentLeg = updatedLegs[legIndex];
    if (!currentLeg) return;

    if (legIndex === 0) {
      // First leg
      updatedLegs[legIndex] = {
        mode: currentLeg.mode || "flight",
        departureTime: currentLeg.departureTime || "",
        arrivalTime: currentLeg.arrivalTime || "",
        cost: currentLeg.cost || 0,
        origin: currentLeg.origin || "",
        destination: selectedLocation.name,
        destinationCode: selectedLocation.airports[0] || "",
        destinationCoordinates: selectedLocation.coordinates,
      };

      // Update next leg's origin
      const nextLeg = updatedLegs[legIndex + 1];
      if (nextLeg) {
        updatedLegs[legIndex + 1] = {
          mode: nextLeg.mode || "flight",
          departureTime: nextLeg.departureTime || "",
          arrivalTime: nextLeg.arrivalTime || "",
          cost: nextLeg.cost || 0,
          destination: nextLeg.destination || "",
          origin: selectedLocation.name,
          originCode: selectedLocation.airports[0] || "",
          originCoordinates: selectedLocation.coordinates,
        };
      }
    } else if (legIndex === formData.legs.length - 1) {
      // Last leg
      updatedLegs[legIndex] = {
        mode: currentLeg.mode || "flight",
        departureTime: currentLeg.departureTime || "",
        arrivalTime: currentLeg.arrivalTime || "",
        cost: currentLeg.cost || 0,
        destination: currentLeg.destination || "",
        origin: selectedLocation.name,
        originCode: selectedLocation.airports[0] || "",
        originCoordinates: selectedLocation.coordinates,
      };

      // Update previous leg's destination
      const prevLeg = updatedLegs[legIndex - 1];
      if (prevLeg) {
        updatedLegs[legIndex - 1] = {
          mode: prevLeg.mode || "flight",
          departureTime: prevLeg.departureTime || "",
          arrivalTime: prevLeg.arrivalTime || "",
          cost: prevLeg.cost || 0,
          origin: prevLeg.origin || "",
          destination: selectedLocation.name,
          destinationCode: selectedLocation.airports[0] || "",
          destinationCoordinates: selectedLocation.coordinates,
        };
      }
    }

    handleInputChange("legs", updatedLegs);
  };

  return (
    <div className="space-y-4 min-h-[300px]">
      <div className="flex flex-col space-y-0 relative">
        <div className="absolute left-[6px] top-6 bottom-6 w-[2px] bg-neutral-200" />
        <div key="origin" className="flex items-center gap-4 relative py-2">
          <div className="w-3.5 border-[2px] border-white h-3.5 rounded-full bg-indigo-600 flex items-center justify-center z-10" />
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-neutral-800">
              {transportData.locationAbove?.name}
            </span>
            {transportData.locationAbove?.airports &&
              transportData.locationAbove.airports.length > 0 && (
                <span className="text-[11px] text-neutral-500">
                  {transportData.locationAbove.airports.join(", ")}
                </span>
              )}
          </div>
        </div>
        {Array.from({ length: stopovers }).map((_, i) => (
          <div
            key={`stopover-${i}`}
            className="flex items-center gap-4 relative py-1"
          >
            <div className="w-3.5 border-[2px] border-white h-3.5 rounded-full bg-orange-500 flex items-center justify-center z-10" />
            <div className="relative flex-shrink-0 h-10 w-[300px]">
              <div className="relative h-full w-full">
                <Input
                  type="text"
                  placeholder="Search airport or city"
                  value={getInputValue(i)}
                  onChange={(e) => handleStopoverChange(i, e.target.value)}
                  className="pl-2 pr-8 py-1.5 text-[13px] w-full h-full focus-visible:outline-none focus-visible:ring-0 rounded-lg"
                />
                {isLoading && activeIndex === i && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                  </div>
                )}
                {autocompleteResults.length > 0 &&
                  activeIndex === i &&
                  (searchQueries[i]?.length ?? 0) >= MIN_SEARCH_LENGTH && (
                    <div className="mt-1 absolute top-full left-0 right-0 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-y-auto max-h-48 no-scrollbar z-20">
                      {autocompleteResults.map((airport) => (
                        <button
                          key={`${airport.code}-${i}`}
                          onClick={() => handleAirportSelect(i, airport)}
                          className="w-full p-2 flex items-center gap-2 hover:bg-neutral-50 active:bg-neutral-100 transition-colors text-left"
                        >
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">
                              {airport.name}
                            </span>
                            <span className="text-[10px] text-neutral-500">
                              {airport.code}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
        ))}
        <div
          key="destination"
          className="flex items-center gap-4 relative py-2"
        >
          <div className="w-3.5 border-[2px] border-white h-3.5 rounded-full bg-indigo-600 flex items-center justify-center z-10" />
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-neutral-800">
              {transportData.locationBelow?.name}
            </span>
            {transportData.locationBelow?.airports &&
              transportData.locationBelow.airports.length > 0 && (
                <span className="text-[11px] text-neutral-500">
                  {transportData.locationBelow.airports.join(", ")}
                </span>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StopoverSelector;
